'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import { BASE_LINE_HEIGHT } from 'HubStyleTokens/sizes';
import { BASE_LINK_COLOR, BASE_LINK_HOVER_COLOR } from 'HubStyleTokens/theme';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import styled, { css } from 'styled-components';
import UIClickable from '../button/UIClickable';
import SyntheticEvent from '../core/SyntheticEvent';
import Controllable from '../decorators/Controllable';
import { CARET_HEIGHT, CARET_WIDTH } from '../icon/IconConstants';
import UICaret from '../icon/icons/UICaret';
import AccordionTransition from '../transitions/AccordionTransition';
import memoWithDisplayName from '../utils/memoWithDisplayName';
import { allSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import { setUiTransition } from '../utils/Styles';
const indicatorCanvasStyle = {
  display: 'block',
  width: '100%',
  visibility: 'hidden'
};
const indicatorCaretStyle = {
  position: 'absolute',
  width: '100%',
  left: 0,
  top: 0
};
const indicatorCoordinatesForSize = {
  xs: 'top: 7px; width: 5px;',
  sm: 'top: 3px; width: 7px;',
  md: 'top: 4px; width: 8px;',
  lg: 'top: 3px; width: 11px;',
  xl: 'top: 10px; width: 11px;'
};
const Indicator = styled.div.withConfig({
  displayName: "UIAccordionItem__Indicator",
  componentId: "sc-9c4qcj-0"
})(["stroke:", ";position:absolute;left:0;", ";transform:", ";", ";"], BASE_LINK_COLOR, ({
  size
}) => indicatorCoordinatesForSize[size], ({
  open
}) => open && 'rotate(90deg)', setUiTransition());
const ExtraSmallHeading = styled.div.withConfig({
  displayName: "UIAccordionItem__ExtraSmallHeading",
  componentId: "sc-9c4qcj-1"
})(["margin-bottom:8px;"]);
const headingForSize = {
  xs: ExtraSmallHeading,
  sm: 'h5',
  md: 'h4',
  lg: 'h2',
  xl: 'h1'
};
const paddingLeftForSize = {
  xs: 17,
  sm: 23,
  md: 27,
  lg: 30,
  xl: 30
};
const paddingLeftMixin = css(["padding-left:", "px;"], ({
  size
}) => paddingLeftForSize[size]);
const Clickable = styled((_ref) => {
  let rest = _objectWithoutPropertiesLoose(_ref, ["open", "size"]);

  return /*#__PURE__*/_jsx(UIClickable, Object.assign({}, rest));
}).withConfig({
  displayName: "UIAccordionItem__Clickable",
  componentId: "sc-9c4qcj-2"
})(["display:block;position:relative;line-height:", ";", ";user-select:none;&:hover{color:", ";", "{color:currentColor;}}"], BASE_LINE_HEIGHT, paddingLeftMixin, BASE_LINK_HOVER_COLOR, Indicator);
const Transition = styled((_ref2) => {
  let rest = _objectWithoutPropertiesLoose(_ref2, ["size"]);

  return /*#__PURE__*/_jsx(AccordionTransition, Object.assign({}, rest));
}).withConfig({
  displayName: "UIAccordionItem__Transition",
  componentId: "sc-9c4qcj-3"
})(["", ";"], paddingLeftMixin);
const UIAccordionItem = memoWithDisplayName('UIAccordionItem', props => {
  const {
    animate,
    children,
    Content,
    renderContent,
    contentClassName,
    disabled,
    flush,
    onOpenChange,
    open,
    size,
    title,
    titleClassName
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["animate", "children", "Content", "renderContent", "contentClassName", "disabled", "flush", "onOpenChange", "open", "size", "title", "titleClassName"]);

  const handleTitleClick = useCallback(() => {
    onOpenChange(SyntheticEvent(!open));
  }, [onOpenChange, open]);
  const shorthandSize = toShorthandSize(size);
  const computedTitleClassName = classNames(titleClassName, 'private-accordion');
  const Heading = headingForSize[shorthandSize];
  return /*#__PURE__*/_jsxs("div", Object.assign({}, rest, {
    children: [/*#__PURE__*/_jsx(Clickable, {
      "aria-expanded": open,
      className: computedTitleClassName,
      disabled: disabled,
      onClick: handleTitleClick,
      open: open,
      size: shorthandSize,
      children: /*#__PURE__*/_jsxs(Heading, {
        role: "presentation",
        className: flush ? 'm-bottom-0' : '',
        children: [/*#__PURE__*/_jsxs(Indicator, {
          open: open,
          size: shorthandSize,
          children: [/*#__PURE__*/_jsx("canvas", {
            width: CARET_WIDTH,
            height: CARET_HEIGHT,
            style: indicatorCanvasStyle
          }), /*#__PURE__*/_jsx(UICaret, {
            style: indicatorCaretStyle,
            viewBoxHeight: CARET_HEIGHT,
            viewBoxWidth: CARET_WIDTH,
            width: 12
          })]
        }), title]
      })
    }), /*#__PURE__*/_jsx(Transition, {
      Content: Content,
      renderContent: renderContent,
      duration: animate ? undefined : 0,
      className: classNames("UIAccordionItem-content private-accordion__content", contentClassName),
      open: open,
      size: shorthandSize,
      children: children
    })]
  }));
});
UIAccordionItem.propTypes = {
  animate: PropTypes.bool.isRequired,
  children: PropTypes.node,
  Content: PropTypes.elementType,
  renderContent: PropTypes.func,
  contentClassName: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  flush: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func,
  open: PropTypes.bool,
  title: PropTypes.node.isRequired,
  titleClassName: PropTypes.string,
  size: allSizes
};
UIAccordionItem.defaultProps = {
  animate: true,
  disabled: false,
  flush: false,
  open: false,
  size: 'xs'
};
export default Controllable(UIAccordionItem, ['open']);